import { useState, useEffect, useRef } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { useToast } from "@/hooks/use-toast";
import { formatDistance, formatDistanceToNow } from "date-fns";
import { copyFormattedContent } from "@/lib/utils";
import { useQueryClient } from "@tanstack/react-query";

import { Card, CardContent, CardHeader } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Skeleton } from "@/components/ui/skeleton";
import { MessagesSquare } from "lucide-react";
import { AlertCircle } from "lucide-react";

import {
  ArrowUpFromLine,
  ArrowDownFromLine,
  MessageSquare,
  Share,
  UserRound,
  ArrowLeft,
  Save,
  Clipboard,
  ClipboardCheck,
  Eye,
  Pen,
  Pencil,
  FileText,
} from "lucide-react";

const RedditPostEditor = () => {
  const { projectId: urlProjectId, postId, scheduledPostId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { toast } = useToast();
  const [isLoading, setIsLoading] = useState(true);
  const [activeTab, setActiveTab] = useState<"preview" | "edit">("preview");
  const [copied, setCopied] = useState(false);
  const originalProjectIdRef = useRef<string | undefined>(urlProjectId);
  const queryClient = useQueryClient();

  // Determine if we're editing a scheduled post or generated post
  const isEditingScheduled = !!scheduledPostId;
  const targetId = scheduledPostId || postId;

  // Helper function to safely access localStorage
  const safelyStoreProjectId = (id: string) => {
    try {
      localStorage.setItem('redditEditorProjectId', id);
    } catch (error) {
      // Silent fail if localStorage is not available
      console.warn('Could not access localStorage', error);
    }
  };
  
  const safelyGetProjectId = () => {
    try {
      return localStorage.getItem('redditEditorProjectId');
    } catch (error) {
      console.warn('Could not access localStorage', error);
      return null;
    }
  };

  // Store projectId in localStorage when component mounts
  useEffect(() => {
    if (urlProjectId) {
      safelyStoreProjectId(urlProjectId);
    }
  }, [urlProjectId]);

  // Post state - updated to handle both types
  const [post, setPost] = useState<{
    id: string;
    title: string;
    content: string;
    subreddit: string;
    postType: string;
    hiddenGoal: string;
    createdAt: string;
    scheduledAt?: string;
    status?: string;
    isScheduled?: boolean;
    metadata?: {
      subreddit?: string;
      userId?: string;
      type?: string;
      hiddenGoal?: string;
    };
  } | null>(null);

  // Editor state
  const [title, setTitle] = useState("");
  const [content, setContent] = useState("");
  const [isSaving, setIsSaving] = useState(false);
  
  // Simulated vote count (random but deterministic based on post id)
  const upvotes = targetId ? Math.abs(targetId.split('').reduce((acc, char) => acc + char.charCodeAt(0), 0) % 1000) + 10 : 42;

  // Fetch post data - updated to handle both types
  useEffect(() => {
    const fetchPost = async () => {
      if (!targetId || !user?.id) return;

      try {
        setIsLoading(true);
        
        if (isEditingScheduled) {
          // Fetch from scheduled_reddit_posts table
          const { data, error } = await supabase
            .from("scheduled_reddit_posts")
            .select()
            .eq("id", targetId)
            .eq("user_id", user.id)
            .single();

          if (error) throw error;

          if (data) {
            const postData = {
              id: data.id,
              title: data.post_title || "",
              content: data.post_content || "",
              subreddit: data.subreddit_name || "unknown",
              postType: "Scheduled Post",
              hiddenGoal: "None",
              createdAt: data.created_at
                ? formatDistance(new Date(data.created_at), new Date(), { addSuffix: true })
                : "recently",
              scheduledAt: data.scheduled_at_utc
                ? formatDistance(new Date(data.scheduled_at_utc), new Date(), { addSuffix: true })
                : undefined,
              status: data.status,
              isScheduled: true,
              metadata: {
                subreddit: data.subreddit_name,
                type: "Scheduled Post"
              }
            };
            
            setPost(postData);
            setTitle(postData.title);
            setContent(postData.content);
          }
        } else {
          // Fetch from generated_content table (existing logic)
        const { data, error } = await supabase
          .from("generated_content")
          .select()
            .eq("id", targetId)
          .eq("user_id", user.id)
          .single();

        if (error) throw error;

        if (data) {
          // Type assertion for metadata to handle subreddit, topic, and hiddenGoal
          const metadata = data.metadata as Record<string, any> || {};
          
          const postData = {
            id: data.id,
            title: data.title || "",
            content: data.content || "",
            subreddit: metadata.subreddit || "unknown",
            postType: metadata.topic || "Post",
            hiddenGoal: metadata.hiddenGoal || "None",
            createdAt: data.created_at
              ? formatDistance(new Date(data.created_at), new Date(), { addSuffix: true })
              : "recently",
              isScheduled: false,
            metadata: metadata,
          };
          
          setPost(postData);
          setTitle(postData.title);
          setContent(postData.content);
          }
        }
      } catch (error) {
        console.error("Error fetching post:", error);
        toast({
          title: "Error",
          description: `Failed to load the ${isEditingScheduled ? 'scheduled' : 'Reddit'} post`,
          variant: "destructive",
        });
      } finally {
        setIsLoading(false);
      }
    };

    fetchPost();
  }, [targetId, user?.id, toast, isEditingScheduled]);

  // Save post changes - updated to handle both types
  const savePost = async () => {
    if (!targetId || !user?.id) return;

    try {
      setIsSaving(true);
      
      if (isEditingScheduled) {
        // Update scheduled_reddit_posts table
        const { error } = await supabase
          .from("scheduled_reddit_posts")
          .update({
            post_title: title,
            post_content: content,
          })
          .eq("id", targetId)
          .eq("user_id", user.id);

        if (error) throw error;

        toast({
          title: "Saved",
          description: "Your scheduled Reddit post has been updated",
        });
      } else {
        // Update generated_content table (existing logic)
      const { error } = await supabase
        .from("generated_content")
        .update({
          title,
          content,
        })
          .eq("id", targetId)
        .eq("user_id", user.id);

      if (error) throw error;

        toast({
          title: "Saved",
          description: "Your Reddit post has been updated",
        });
      }

      // Update local state
      setPost(prev => prev ? { ...prev, title, content } : null);
      
      // Switch to preview tab
      setActiveTab("preview");
      
      // Invalidate queries to keep UI in sync
      if (isEditingScheduled) {
        queryClient.invalidateQueries({ queryKey: ['scheduled-reddit-posts', urlProjectId, user.id] });
      } else {
        queryClient.invalidateQueries({ queryKey: ['generated-content', urlProjectId, 'reddit'] });
      }
      
    } catch (error) {
      console.error("Error saving post:", error);
      toast({
        title: "Error",
        description: `Failed to save the ${isEditingScheduled ? 'scheduled' : 'Reddit'} post`,
        variant: "destructive",
      });
    } finally {
      setIsSaving(false);
    }
  };

  // Copy content to clipboard
  const copyToClipboard = () => {
    const fullPost = `${title}\n\n${content}`;
    
    if (!fullPost.trim()) {
      toast({
        title: "Cannot copy",
        description: "There is no content to copy.",
        variant: "destructive"
      });
      return;
    }
    
    copyFormattedContent(fullPost, {
      onSuccess: () => {
        setCopied(true);
        toast({
          title: "Copied to clipboard",
          description: "Content has been copied to clipboard",
        });
        setTimeout(() => setCopied(false), 2000);
      },
      onError: (error) => {
        console.error("Copy failed:", error);
        toast({
          title: "Failed to copy",
          description: "Could not copy content to clipboard",
          variant: "destructive",
        });
      }
    });
  };

  // Go back to content manager page
  const goBack = () => {
    // Always use the original project ID from when the component first mounted
    const id = originalProjectIdRef.current || safelyGetProjectId();
    
    if (id) {
      navigate(`/projects/${id}/reddit/content`);
    } else {
      // If no projectId available, try to go back in history as last resort
      console.warn("Project ID not available, using browser history fallback");
      navigate(-1);
    }
  };

  // Loading state
  if (isLoading) {
    return (
      <div className="container py-6">
        <Card className="border-zinc-200 dark:border-zinc-800">
          <CardHeader>
            <div className="flex items-center gap-4">
              <div>
                <Skeleton className="h-8 w-64 mb-2" />
                <Skeleton className="h-4 w-48" />
              </div>
            </div>
          </CardHeader>
          <CardContent>
            <div className="space-y-6 p-4">
              <Skeleton className="h-12 w-64 mb-6" />
              <div className="grid gap-4">
                <Skeleton className="h-24 w-full" />
                <Skeleton className="h-24 w-full" />
                <Skeleton className="h-48 w-full" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Error state
  if (!post) {
    return (
      <div className="container py-6">
        <Card className="border-zinc-200 dark:border-zinc-800">
          <CardHeader>
            <div className="flex items-center gap-4">
              <div>
                <h1 className="text-3xl font-bold text-zinc-900 dark:text-zinc-100">
                  Reddit Post Editor
                </h1>
                <p className="text-zinc-500 dark:text-zinc-400">
                  Edit your Reddit post before publishing
                </p>
              </div>
            </div>
          </CardHeader>
          <CardContent className="flex flex-col items-center justify-center py-12">
            <div className="rounded-full bg-red-100 dark:bg-red-900/20 p-3 mb-4">
              <AlertCircle className="h-6 w-6 text-red-500" />
            </div>
            <h3 className="text-xl font-medium mb-2">Post Not Found</h3>
            <p className="text-muted-foreground mb-6 max-w-md text-center">
              The Reddit post you're looking for could not be found. It may have been deleted or there was an error loading it.
            </p>
            <Button 
              onClick={goBack}
              variant="outline"
            >
              Back to Reddit Posts
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="container py-6 max-w-7xl">
      <div className="flex flex-col space-y-8">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <div>
              <h1 className="text-3xl font-bold tracking-tight bg-gradient-to-r from-blue-500 to-blue-500 bg-clip-text text-transparent">
                {post?.isScheduled ? 'Edit Scheduled Post' : 'Edit Reddit Post'}
              </h1>
              <p className="text-zinc-500 mt-1">
                {post?.isScheduled 
                  ? `Edit your scheduled post for r/${post.subreddit}` 
                  : 'Edit and preview your Reddit post'
                }
              </p>
              {post?.isScheduled && post.scheduledAt && (
                <p className="text-sm text-amber-600 dark:text-amber-400 mt-1">
                  📅 Scheduled to post {post.scheduledAt}
                </p>
              )}
              {post?.isScheduled && post.status && (
                <Badge className="mt-2" variant={
                  post.status === 'sent' ? 'default' : 
                  post.status === 'error_posting' ? 'destructive' : 
                  'secondary'
                }>
                  Status: {post.status.replace(/_/g, ' ')}
                </Badge>
              )}
            </div>
          </div>
          
          <div className="flex items-center gap-3">
            <Button
              variant="outline"
              size="sm"
              onClick={goBack}
              className="gap-1.5"
            >
              <ArrowLeft className="h-4 w-4" />
              Back to Content
            </Button>
            <Button
              onClick={savePost}
              disabled={isSaving}
              className="gap-1.5 bg-gradient-to-r from-blue-500 to-blue-500 hover:from-blue-600 hover:to-blue-600"
            >
              {isSaving ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                  Saving...
                </>
              ) : (
                <>
                  <Save className="h-4 w-4" />
                  Save Changes
                </>
              )}
            </Button>
          </div>
        </div>

        {/* Main Editor Card */}
        <Card className="overflow-hidden border-zinc-200 dark:border-zinc-800">
          <CardContent className="p-6">
          {/* Editor/Preview Tabs */}
          <Tabs value={activeTab} onValueChange={(value) => setActiveTab(value as "preview" | "edit")}>
            <TabsList className="mb-4 bg-zinc-100 dark:bg-zinc-800/50">
              <TabsTrigger
                value="preview"
                className={activeTab === "preview" ? "text-blue-600 dark:text-blue-400" : ""}
              >
                <Eye className="h-4 w-4 mr-1.5" />
                Preview
              </TabsTrigger>
              <TabsTrigger
                value="edit"
                className={activeTab === "edit" ? "text-blue-600 dark:text-blue-400" : ""}
              >
                <Pencil className="h-4 w-4 mr-1.5" />
                Edit
              </TabsTrigger>
            </TabsList>
            
            {/* Preview Tab */}
            <TabsContent value="preview" className="mt-0">
              <Card className="overflow-hidden border-blue-200 dark:border-blue-800/30 shadow-md">
                <CardContent className="p-0">
                  <div className="flex">
                    {/* Voting sidebar */}
                    <div className="flex flex-col items-center px-2 py-4 bg-zinc-50 dark:bg-zinc-900/70 min-w-[50px]">
                      <Button variant="ghost" size="sm" className="h-6 w-6 rounded-full text-blue-500 hover:text-blue-600">
                        <ArrowUpFromLine className="h-5 w-5" />
                      </Button>
                      <span className="text-sm font-medium my-1">{upvotes}</span>
                      <Button variant="ghost" size="sm" className="h-6 w-6 rounded-full text-zinc-400 hover:text-zinc-500">
                        <ArrowDownFromLine className="h-5 w-5" />
                      </Button>
                    </div>
                    
                    {/* Main content */}
                    <div className="flex-1 p-3">
                      {/* Subreddit and user info */}
                      <div className="bg-zinc-100 dark:bg-zinc-900/70 p-2.5 flex items-center text-sm">
                        <div className="text-blue-600 dark:text-blue-400 font-medium">
                          r/{post.metadata?.subreddit || post.subreddit || "unknown"}
                        </div>
                        <div className="ml-2 text-zinc-500 dark:text-zinc-400 text-xs">
                          {post.isScheduled ? (
                            <>
                              Scheduled by u/{post.metadata?.userId || "you"} • 
                              {post.scheduledAt ? ` to post ${post.scheduledAt}` : ' scheduled'}
                            </>
                          ) : (
                            <>Posted by u/{post.metadata?.userId || "anonymous"} {post.createdAt && formatDistanceToNow(new Date(), { addSuffix: true })}</>
                          )}
                        </div>
                      </div>
                      
                      {/* Post title */}
                      <h3 className="text-xl font-medium my-3">{post.title}</h3>
                      
                      {/* Post content */}
                      <div className="whitespace-pre-wrap text-sm mb-3">{post.content}</div>
                      
                      {/* Tags */}
                      <div className="flex flex-wrap gap-2 mt-4">
                        <Badge 
                          variant="outline" 
                          className="ml-2 border-blue-200 dark:border-blue-800 bg-blue-50 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300"
                        >
                          {post.metadata?.type || "Text Post"}
                        </Badge>
                        {post.metadata?.hiddenGoal && post.metadata.hiddenGoal !== "None" && (
                          <Badge 
                            variant="outline" 
                            className="ml-1.5 border-blue-200 dark:border-blue-800 bg-blue-50 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300"
                          >
                            Goal: {post.metadata.hiddenGoal}
                          </Badge>
                        )}
                        {post.isScheduled && post.status && (
                          <Badge 
                            variant="outline" 
                            className="ml-1.5 border-amber-200 dark:border-amber-800 bg-amber-50 dark:bg-amber-900/30 text-amber-700 dark:text-amber-300"
                          >
                            Status: {post.status.replace(/_/g, ' ')}
                          </Badge>
                        )}
                      </div>
                      
                      {/* Footer with comment count */}
                      <div className="flex items-center mt-6 text-sm text-zinc-500 dark:text-zinc-400">
                        <MessageSquare className="h-4 w-4 mr-1.5" />
                        0 comments
                        <Share className="h-4 w-4 ml-4 mr-1.5" />
                        Share
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </TabsContent>
            
            {/* Edit Tab */}
            <TabsContent value="edit" className="mt-0">
              <Card className="overflow-hidden border-blue-200 dark:border-blue-800 shadow-md">
                <CardContent className="p-4">
                  <div className="mb-4">
                    <div className="flex items-center gap-2 mb-2 text-xs text-zinc-500">
                      <span className="font-medium text-zinc-900 dark:text-zinc-200">
                        r/{post.metadata?.subreddit || post.subreddit}
                      </span>
                      <span className="inline-flex items-center gap-1">
                        <span className="text-xs">{post.isScheduled ? 'Scheduled by' : 'Posted by'}</span>
                        <UserRound className="h-3 w-3" />
                        <span className="text-xs">you</span>
                      </span>
                    </div>
                    
                    {/* Title input */}
                    <div className="mb-4">
                      <Input
                        value={title}
                        onChange={(e) => setTitle(e.target.value)}
                        placeholder="Post title"
                        className="text-lg font-medium mb-2 focus-visible:ring-blue-500"
                      />
                    </div>
                    
                    {/* Content textarea */}
                    <Textarea
                      value={content}
                      onChange={(e) => setContent(e.target.value)}
                      placeholder="Text (optional)"
                      className="min-h-[200px] focus-visible:ring-blue-500"
                    />
                    
                    {/* Tags reminder */}
                    <div className="mt-4 text-xs text-zinc-500">
                      <span className="font-medium">Post type:</span> {post.postType}
                      {post.hiddenGoal !== "None" && post.hiddenGoal !== "There's no hidden goal" && (
                        <>
                          <span className="mx-2">•</span>
                          <span className="font-medium">Goal:</span> {post.hiddenGoal}
                        </>
                      )}
                      {post.isScheduled && (
                        <>
                          <span className="mx-2">•</span>
                          <span className="font-medium">Status:</span> {post.status?.replace(/_/g, ' ')}
                        </>
                      )}
                    </div>
                  </div>
                  
                  <div className="flex items-center justify-end gap-2 mt-4 pt-3 border-t border-zinc-200 dark:border-zinc-800">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => setActiveTab("preview")}
                    >
                      Cancel
                    </Button>
                    <Button
                      onClick={savePost}
                      disabled={isSaving}
                      size="sm"
                      className="gap-1.5 bg-gradient-to-r from-blue-500 to-blue-500 hover:from-blue-600 hover:to-blue-600"
                    >
                      {isSaving ? "Saving..." : (
                        <>
                          <Save className="h-4 w-4" />
                          Save
                        </>
                      )}
                    </Button>
                  </div>
                </CardContent>
              </Card>
            </TabsContent>
          </Tabs>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default RedditPostEditor; 